/** Canonical complaint workflow statuses (API + UI). */
export const COMPLAINT_STATUS_VALUES = [
  'pending',
  'under_review',
  'resolved',
  'archived',
  'transferred',
];

export const COMPLAINT_STATUS_SELECT_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'archived', label: 'Archived' },
  { value: 'transferred', label: 'Transferred' },
];

export const Category_STATUS_SELECT_OPTIONS = [
  { value: 'inprogress_complaints', label: 'In-Progress' },
  { value: 'overdue_complaints', label: 'Overdue' },
  { value: 'resolved_complaints', label: 'Resolved' },
];

const STATUS_ALIASES = {
  pending: 'pending',
  under_review: 'under_review',
  resolved: 'resolved',
  archived: 'archived',
  archieved: 'archived',
  transferred: 'transferred',
};

const LABEL_BY_VALUE = Object.fromEntries(
  COMPLAINT_STATUS_SELECT_OPTIONS.map((o) => [o.value, o.label])
);

/** Badge colors keyed by display label from `formatComplaintStatusLabel`. */
export const COMPLAINT_STATUS_BADGE_CLASSES = {
  Pending: 'bg-red-50 text-red-700',
  'Under Review': 'bg-orange-50 text-orange-700',
  Resolved: 'bg-emerald-50 text-emerald-700',
  Archived: 'bg-slate-100 text-slate-700',
  Transferred: 'bg-blue-50 text-blue-700',
};

export function getComplaintStatusBadgeClass(displayLabel) {
  return COMPLAINT_STATUS_BADGE_CLASSES[displayLabel] || 'bg-gray-100 text-gray-700';
}

/**
 * Normalize raw API / legacy status to one of `COMPLAINT_STATUS_VALUES`, or '' if unknown.
 */
export function normalizeComplaintStatusKey(statusOrRow) {
  const v =
    statusOrRow != null && typeof statusOrRow === 'object'
      ? statusOrRow.status
      : statusOrRow;
  if (v == null || v === '') return '';
  if (typeof v === 'boolean') return v ? 'pending' : 'pending';
  if (typeof v === 'number') {
    if (v === 0) return 'pending';
    if (v === 1) return 'under_review';
    if (v === 2) return 'resolved';
    if (v === 3) return 'archived';
    if (v === 4) return 'transferred';
    return '';
  }
  const s = String(v).trim().toLowerCase().replace(/\s+/g, '_');
  return STATUS_ALIASES[s] || (COMPLAINT_STATUS_VALUES.includes(s) ? s : '');
}

/** Human-readable label for tables and detail (e.g. `under_review` → `Under Review`). */
export function formatComplaintStatusLabel(status) {
  const key = normalizeComplaintStatusKey(status);
  if (!key) return LABEL_BY_VALUE.pending;
  return LABEL_BY_VALUE[key] || 'Pending';
}

/**
 * Status string for complaint create/update payloads and comment posts.
 */
export function complaintStatusForPayload(isEditing, raw) {
  if (!isEditing) return 'pending';
  const key = normalizeComplaintStatusKey(raw);
  return key || 'pending';
}

/** Status on the complaint row (multiple API field names). */
export function complaintRowStatusKey(raw) {
  if (!raw || typeof raw !== 'object') return '';
  return (
    normalizeComplaintStatusKey(raw) ||
    normalizeComplaintStatusKey(raw.status_name) ||
    normalizeComplaintStatusKey(raw.complaint_status) ||
    normalizeComplaintStatusKey(raw.current_status)
  );
}

/** Newest status from `complaint_comments_details` (post-update workflow). */
export function complaintLatestCommentStatusKey(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const list = raw.complaint_comments_details;
  if (!Array.isArray(list) || list.length === 0) return '';

  const sorted = [...list].sort((a, b) => {
    const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });

  for (const c of sorted) {
    const key = normalizeComplaintStatusKey(c?.status);
    if (key) return key;
  }
  return '';
}

/**
 * Effective status for ratings / terminal lock: latest comment when present, else row.
 */
export function complaintEffectiveStatusKey(raw) {
  const fromComment = complaintLatestCommentStatusKey(raw);
  if (fromComment) return fromComment;
  return complaintRowStatusKey(raw);
}

export function isComplaintStatusPending(raw) {
  const key = complaintEffectiveStatusKey(raw);
  return !key || key === 'pending';
}

export function complaintRaisedByAccountIdFromRaw(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const candidates = [
    raw.complaint_raised_by,
    raw.raised_by,
    raw.raisedBy,
    raw.created_by_id,
    raw.created_by,
    raw.account_id,
  ];
  for (const value of candidates) {
    if (value == null || value === '') continue;
    const s = String(value).trim();
    if (!s) continue;
    if (/^\d+$/.test(s)) return s;
  }
  return '';
}

export function isComplaintRaisedByAccount(raw, accountId) {
  const uid = String(accountId ?? '').trim();
  const raiser = complaintRaisedByAccountIdFromRaw(raw);
  return Boolean(uid && raiser && raiser === uid);
}


export function canAccountEditComplaint(raw, accountId, hasEditPermission) {
  if (!hasEditPermission) return false;
  if (!isComplaintRaisedByAccount(raw, accountId)) return true;
  return isComplaintStatusPending(raw);
}


export function complaintStatusSelectValue(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const fromRow = complaintRowStatusKey(raw);
  if (fromRow) return fromRow;
  return complaintLatestCommentStatusKey(raw);
}

export function isAllowedComplaintStatus(value) {
  return COMPLAINT_STATUS_VALUES.includes(normalizeComplaintStatusKey(value));
}

const TERMINAL_COMPLAINT_STATUS_KEYS = new Set(['resolved', 'archived']);


export function complaintEverResolvedOrArchived(raw) {
  if (!raw || typeof raw !== 'object') return false;
  return TERMINAL_COMPLAINT_STATUS_KEYS.has(complaintEffectiveStatusKey(raw));
}
